/**
 * hangingProtocols.js
 *
 * Industry-standard hanging protocols for GQG Viewer.
 *
 * Each protocol defines:
 *   - id / name / priority
 *   - matchingRules: how to select this protocol for a study
 *   - layout: { rows, cols }
 *   - viewports[]: ordered viewport slot definitions
 *       - seriesSelector: how to pick the series for this slot
 *       - wl: optional initial window/level { ww, wc }  (0/0 = server auto)
 *       - label: overlay label for the viewport
 *
 * Protocols are ranked by score; highest wins.
 * Priority acts as a tiebreaker.
 */

// ─── W/L Presets ─────────────────────────────────────────────────────────────
// Standard radiological window / level presets.
export const WL = {
  AUTO:         { ww: 0,    wc: 0    },  // server picks best
  CT_BRAIN:     { ww: 80,   wc: 40   },
  CT_SUBDURAL:  { ww: 200,  wc: 80   },
  CT_BONE:      { ww: 2000, wc: 300  },
  CT_SOFT:      { ww: 600,  wc: 40   },  // wider than 400 — handles noisy/spectral series better
  CT_LUNG:      { ww: 1500, wc: -600 },
  CT_LIVER:     { ww: 150,  wc: 80   },
  CT_ANGIO:     { ww: 500,  wc: 250  },
  CT_SPINE:     { ww: 600,  wc: 40   },
  CT_BONE_FINE: { ww: 1800, wc: 400  },
  MR_T1:        { ww: 0,    wc: 0    },  // auto
  MR_T2:        { ww: 0,    wc: 0    },  // auto
  MR_FLAIR:     { ww: 0,    wc: 0    },  // auto
  PET:          { ww: 0,    wc: 0    },  // auto
  CR_DX:        { ww: 0,    wc: 0    },  // auto
  US:           { ww: 0,    wc: 0    },  // auto
  MG:           { ww: 0,    wc: 0    },  // auto
};

// ─── Series Selector helpers ──────────────────────────────────────────────────
// Each series selector is a spec that seriesForSlot() evaluates against the
// available series list to find the best match.

// Slot: pick the series whose description best matches the given keyword list.
// Falls back to any series with minInstances or the first series if nothing matches.
const slot = (spec) => spec;

// ─── Protocol Definitions ─────────────────────────────────────────────────────

const protocols = [

  // ──────────────────────────────────────────────────────────────────────────
  // CT CHEST
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'ct-chest',
    name: 'CT Chest',
    priority: 20,
    matchingRules: {
      modalities: ['CT'],
      bodyPartKeywords: ['chest', 'thorax', 'lung', 'pulmon', 'mediastin', 'pleural'],
    },
    layout: { rows: 1, cols: 2 },
    viewports: [
      {
        label: 'SOFT TISSUE',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['soft tissue', 'standard', 'mediastin', 'routine', 'chest'],
          exclude:  ['scout', 'localizer', 'gated', 'cor', 'sag'],
          minInstances: 20,
          fallback: 'largest',
        }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'LUNG',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['lung', 'pulmon', 'high resolution', 'hrct'],
          exclude:  ['scout', 'localizer', 'gated', 'cor', 'sag', 'coronal', 'sagittal'],
          minInstances: 20,
          allowDupe: true,  // may show same series at different W/L
          fallback: 'largest',
        }),
        wl: WL.CT_LUNG,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // CT HEAD / BRAIN
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'ct-brain',
    name: 'CT Brain',
    priority: 20,
    matchingRules: {
      modalities: ['CT'],
      bodyPartKeywords: ['brain', 'head', 'skull', 'cranial', 'neuro', 'intracran'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'BRAIN',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['brain', 'standard', 'routine', 'head'],
          exclude:  ['bone', 'scout', 'localizer'],
          minInstances: 10,
          fallback: 'largest',
        }),
        wl: WL.CT_BRAIN,
      },
      {
        label: 'SUBDURAL / BLOOD',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['subdural', 'blood', 'hemorrhage', 'trauma', 'brain', 'standard', 'routine', 'head'],
          exclude:  ['bone', 'scout', 'localizer'],
          minInstances: 10,
          allowDupe: true,
          fallback: 'largest',
        }),
        wl: WL.CT_SUBDURAL,
      },
      {
        label: 'BONE',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['bone', 'skull', 'head'],
          exclude:  ['scout', 'localizer'],
          minInstances: 10,
          allowDupe: true,
          fallback: 'largest',
        }),
        wl: WL.CT_BONE,
      },
      {
        label: 'COR / SAG',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['coronal', 'sagittal', 'cor', 'sag'],
          exclude:  ['scout', 'localizer'],
          minInstances: 10,
          fallback: 'second-largest',
        }),
        wl: WL.CT_BRAIN,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // CT ABDOMEN / PELVIS
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'ct-abdomen',
    name: 'CT Abdomen / Pelvis',
    priority: 20,
    matchingRules: {
      modalities: ['CT'],
      bodyPartKeywords: ['abdomen', 'pelvis', 'liver', 'renal', 'kidney', 'bowel', 'colon', 'pancrea'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'AXIAL',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['portal venous', 'venous', 'axial', 'standard', 'abdomen'],
          exclude:  ['scout', 'localizer', 'bone'],
          minInstances: 30,
          fallback: 'largest',
        }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'ARTERIAL',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['arterial', 'art', 'angio'],
          exclude:  ['scout', 'localizer'],
          minInstances: 20,
          fallback: 'second-largest',
        }),
        wl: WL.CT_ANGIO,
      },
      {
        label: 'CORONAL',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['coronal'],
          exclude:  ['scout', 'localizer'],
          minInstances: 20,
          fallback: 'third-largest',
        }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'DELAYED / OTHER',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['delay', 'nephrograph', 'excretory'],
          exclude:  ['scout', 'localizer'],
          minInstances: 20,
          fallback: 'fourth-largest',
        }),
        wl: WL.CT_SOFT,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // CT SPINE
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'ct-spine',
    name: 'CT Spine',
    priority: 20,
    matchingRules: {
      modalities: ['CT'],
      bodyPartKeywords: ['spine', 'cervical', 'lumbar', 'thoracic', 'sacral', 'vertebr'],
    },
    layout: { rows: 1, cols: 2 },
    viewports: [
      {
        label: 'SOFT TISSUE',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['soft tissue', 'standard', 'axial'],
          exclude:  ['scout', 'localizer', 'gated', 'cor', 'sag'],
          minInstances: 20,
          fallback: 'largest',
        }),
        wl: WL.CT_SPINE,
      },
      {
        label: 'BONE',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['bone', 'osseous'],
          exclude:  ['scout', 'localizer'],
          minInstances: 20,
          allowDupe: true,
          fallback: 'largest',
        }),
        wl: WL.CT_BONE_FINE,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // CT ANGIOGRAPHY (CTA)
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'ct-angio',
    name: 'CT Angiography',
    priority: 25,
    matchingRules: {
      modalities: ['CT'],
      bodyPartKeywords: ['angio', 'cta', 'aorta', 'coronary', 'runoff', 'pulmonary embol'],
    },
    layout: { rows: 1, cols: 2 },
    viewports: [
      {
        label: 'ARTERIAL / RUNOFF',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['arterial', 'angio', 'cta', 'runoff', 'lower extremit', 'vessel', 'aorta'],
          exclude:  ['scout', 'localizer', 'mip', 'cor', 'sag', 'pre contrast', 'pre con'],
          minInstances: 20,
          fallback: 'largest',
        }),
        wl: WL.CT_ANGIO,
      },
      {
        label: 'PRE / SOFT TISSUE',
        seriesSelector: slot({
          modality: 'CT',
          prefer:   ['pre contrast', 'pre con', 'non contrast', 'non con', 'nac', 'soft tissue', 'standard'],
          exclude:  ['scout', 'localizer', 'mip', 'cor', 'sag'],
          minInstances: 20,
          allowDupe: true,
          fallback: 'second-largest',
        }),
        wl: WL.CT_SOFT,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // CT GENERAL (fallback for any CT)
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'ct-general',
    name: 'CT General',
    priority: 5,
    matchingRules: {
      modalities: ['CT'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'SERIES 1',
        seriesSelector: slot({ modality: 'CT', exclude: ['scout', 'localizer', 'gated'], fallback: 'largest' }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'SERIES 2',
        seriesSelector: slot({ modality: 'CT', exclude: ['scout', 'localizer', 'gated'], fallback: 'second-largest' }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'SERIES 3',
        seriesSelector: slot({ modality: 'CT', exclude: ['scout', 'localizer', 'gated'], fallback: 'third-largest' }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'SERIES 4',
        seriesSelector: slot({ modality: 'CT', exclude: ['scout', 'localizer', 'gated'], fallback: 'fourth-largest' }),
        wl: WL.CT_SOFT,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MR BRAIN
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'mr-brain',
    name: 'MR Brain',
    priority: 20,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['brain', 'head', 'cranial', 'neuro', 'orbit'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'FLAIR',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['flair', 'fluid attenuated'],
          exclude:  ['loc', 'localiz', 'scout'],
          fallback: 'first',
        }),
        wl: WL.MR_FLAIR,
      },
      {
        label: 'DWI / ADC',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['dwi', 'diffusion', 'adc', 'trace'],
          exclude:  ['loc', 'localiz', 'scout'],
          fallback: 'second',
        }),
        wl: WL.AUTO,
      },
      {
        label: 'T2',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['t2', 't2w', 'fse t2', 'tse t2', 'turbo t2'],
          exclude:  ['loc', 'localiz', 'scout', 'flair', 'adc', 'dwi'],
          fallback: 'third',
        }),
        wl: WL.MR_T2,
      },
      {
        label: 'T1 / POST',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['post', 't1 post', 't1', 'mprage', 'spgr', 't1w', 'fse t1', 'se t1'],
          exclude:  ['loc', 'localiz', 'scout', 'adc', 'dwi', 'swi', 'bold'],
          fallback: 'fourth',
        }),
        wl: WL.MR_T1,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MR SPINE
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'mr-spine',
    name: 'MR Spine',
    priority: 20,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['spine', 'cervical', 'lumbar', 'thoracic', 'sacral', 'vertebr'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'SAG T2',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['sag t2', 'sagittal t2', 'sag', 't2'],
          exclude:  ['loc', 'localiz', 'scout', 'ax', 'axial', 'cor'],
          fallback: 'largest',
        }),
        wl: WL.MR_T2,
      },
      {
        label: 'AX T2',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['ax t2', 'axial t2', 'ax', 'axial'],
          exclude:  ['loc', 'localiz', 'scout', 'sag', 'cor'],
          fallback: 'second',
        }),
        wl: WL.MR_T2,
      },
      {
        label: 'SAG T1',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['sag t1', 'sagittal t1', 't1'],
          exclude:  ['loc', 'localiz', 'scout', 'ax', 'cor'],
          fallback: 'third',
        }),
        wl: WL.MR_T1,
      },
      {
        label: 'STIR / FS / POST',
        seriesSelector: slot({
          modality: 'MR',
          prefer:   ['stir', 'fat sat', 'fatsat', 'fs', 'post', 'contrast'],
          exclude:  ['loc', 'localiz', 'scout'],
          fallback: 'fourth',
        }),
        wl: WL.AUTO,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MR MUSCULOSKELETAL — BODY-PART-SPECIFIC DEFAULTS
  // Public MSK protocol references consistently prioritize fluid-sensitive
  // fat-suppressed sequences across the clinically relevant planes plus a T1
  // anatomy/marrow sequence. Keep these protocols ahead of the generic MSK
  // fallback so wrist/knee/shoulder/ankle/hip/elbow do not all hang the same way.
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'mr-wrist-hand',
    name: 'MR Wrist / Hand',
    priority: 35,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['wrist', 'hand', 'finger', 'thumb', 'scaphoid', 'tfcc', 'metacarp'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'AX FS',
        seriesSelector: slot({
          modality: 'MR',
          prefer: ['ax t2 fs', 'ax pd fs', 'axial t2 fs', 'axial pd fs', 'ax fspd', 'ax t2 fatsat', 'ax pd fatsat', 'ax t2', 'ax pd', 'axial'],
          exclude: ['loc', 'localiz', 'scout', 'sag', 'cor', 't1'],
          fallback: 'first',
        }),
        wl: WL.AUTO,
      },
      {
        label: 'COR FS',
        seriesSelector: slot({
          modality: 'MR',
          prefer: ['cor t2 fs', 'cor pd fs', 'coronal t2 fs', 'coronal pd fs', 'cor fspd', 'cor stir', 'cor t2 fatsat', 'cor pd fatsat', 'cor t2', 'cor pd'],
          exclude: ['loc', 'localiz', 'scout', 'sag', 'ax', 't1'],
          fallback: 'second',
        }),
        wl: WL.AUTO,
      },
      {
        label: 'COR T1',
        seriesSelector: slot({
          modality: 'MR',
          prefer: ['cor t1', 'coronal t1', 'cor t1 fse', 'cor t1 tse', 't1 cor'],
          exclude: ['loc', 'localiz', 'scout', 'sag', 'ax', 'fat sat', 'fatsat', 'stir'],
          fallback: 'third',
        }),
        wl: WL.MR_T1,
      },
      {
        label: 'SAG FS',
        seriesSelector: slot({
          modality: 'MR',
          prefer: ['sag pd fs', 'sag t2 fs', 'sagittal pd fs', 'sagittal t2 fs', 'sag fspd', 'sag pd fatsat', 'sag t2 fatsat', 'sag stir', 'sag pd', 'sag t2'],
          exclude: ['loc', 'localiz', 'scout', 'cor', 'ax', 't1'],
          fallback: 'fourth',
        }),
        wl: WL.AUTO,
      },
    ],
  },

  {
    id: 'mr-knee',
    name: 'MR Knee',
    priority: 34,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['knee', 'patella', 'meniscus', 'acl', 'pcl'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'SAG PD/T2 FS', seriesSelector: slot({ modality: 'MR', prefer: ['sag pd fs', 'sag t2 fs', 'sagittal pd fs', 'sagittal t2 fs', 'sag fspd', 'sag pd', 'sag t2'], exclude: ['loc', 'localiz', 'scout', 'cor', 'ax'], fallback: 'first' }), wl: WL.AUTO },
      { label: 'COR PD/T2 FS', seriesSelector: slot({ modality: 'MR', prefer: ['cor pd fs', 'cor t2 fs', 'coronal pd fs', 'coronal t2 fs', 'cor fspd', 'cor pd', 'cor t2'], exclude: ['loc', 'localiz', 'scout', 'sag', 'ax'], fallback: 'second' }), wl: WL.AUTO },
      { label: 'AX PD/T2 FS', seriesSelector: slot({ modality: 'MR', prefer: ['ax pd fs', 'ax t2 fs', 'axial pd fs', 'axial t2 fs', 'ax fspd', 'ax pd', 'ax t2'], exclude: ['loc', 'localiz', 'scout', 'sag', 'cor'], fallback: 'third' }), wl: WL.AUTO },
      { label: 'T1', seriesSelector: slot({ modality: 'MR', prefer: ['t1', 'sag t1', 'cor t1'], exclude: ['loc', 'localiz', 'scout'], fallback: 'fourth' }), wl: WL.MR_T1 },
    ],
  },

  {
    id: 'mr-shoulder',
    name: 'MR Shoulder',
    priority: 34,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['shoulder', 'rotator', 'glenoid', 'labral'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'COR OBLIQUE FS', seriesSelector: slot({ modality: 'MR', prefer: ['cor obl pd fs', 'cor oblique pd fs', 'cor t2 fs', 'cor pd fs', 'cor fspd', 'coronal'], exclude: ['loc', 'localiz', 'scout', 'sag', 'ax'], fallback: 'first' }), wl: WL.AUTO },
      { label: 'SAG OBLIQUE', seriesSelector: slot({ modality: 'MR', prefer: ['sag obl', 'sag oblique', 'sag t1', 'sag pd fs', 'sag t2 fs', 'sagittal'], exclude: ['loc', 'localiz', 'scout', 'cor', 'ax'], fallback: 'second' }), wl: WL.AUTO },
      { label: 'AX FS', seriesSelector: slot({ modality: 'MR', prefer: ['ax pd fs', 'ax t2 fs', 'axial pd fs', 'axial t2 fs', 'ax fspd', 'axial'], exclude: ['loc', 'localiz', 'scout', 'sag', 'cor'], fallback: 'third' }), wl: WL.AUTO },
      { label: 'T1 / T2', seriesSelector: slot({ modality: 'MR', prefer: ['t1', 't2'], exclude: ['loc', 'localiz', 'scout'], fallback: 'fourth' }), wl: WL.AUTO },
    ],
  },

  {
    id: 'mr-ankle-foot',
    name: 'MR Ankle / Foot',
    priority: 33,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['ankle', 'foot', 'hindfoot', 'forefoot', 'metatars', 'achilles'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'SAG T1', seriesSelector: slot({ modality: 'MR', prefer: ['sag t1', 'sagittal t1', 't1'], exclude: ['loc', 'localiz', 'scout', 'ax', 'cor'], fallback: 'first' }), wl: WL.MR_T1 },
      { label: 'SAG FS / STIR', seriesSelector: slot({ modality: 'MR', prefer: ['sag stir', 'sag t2 fs', 'sag pd fs', 'sag fs', 'sag fatsat'], exclude: ['loc', 'localiz', 'scout', 'ax', 'cor', 't1'], fallback: 'second' }), wl: WL.AUTO },
      { label: 'AX FS', seriesSelector: slot({ modality: 'MR', prefer: ['ax pd fs', 'ax t2 fs', 'axial pd fs', 'axial t2 fs', 'ax fspd'], exclude: ['loc', 'localiz', 'scout', 'sag', 'cor', 't1'], fallback: 'third' }), wl: WL.AUTO },
      { label: 'COR FS / T1', seriesSelector: slot({ modality: 'MR', prefer: ['cor pd fs', 'cor t2 fs', 'cor stir', 'cor t1', 'coronal'], exclude: ['loc', 'localiz', 'scout', 'sag', 'ax'], fallback: 'fourth' }), wl: WL.AUTO },
    ],
  },

  {
    id: 'mr-hip',
    name: 'MR Hip / Pelvis MSK',
    priority: 33,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['hip', 'pelvis', 'acetabul', 'femoroacetabular', 'hip labrum'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'COR T1', seriesSelector: slot({ modality: 'MR', prefer: ['cor t1', 'coronal t1', 't1 cor'], exclude: ['loc', 'localiz', 'scout', 'ax', 'sag'], fallback: 'first' }), wl: WL.MR_T1 },
      { label: 'COR STIR / FS', seriesSelector: slot({ modality: 'MR', prefer: ['cor stir', 'cor t2 fs', 'cor pd fs', 'cor fs', 'cor fatsat'], exclude: ['loc', 'localiz', 'scout', 'ax', 'sag', 't1'], fallback: 'second' }), wl: WL.AUTO },
      { label: 'AX OBLIQUE / FS', seriesSelector: slot({ modality: 'MR', prefer: ['ax obl', 'ax oblique', 'ax t2 fs', 'ax pd fs', 'axial'], exclude: ['loc', 'localiz', 'scout', 'cor', 'sag'], fallback: 'third' }), wl: WL.AUTO },
      { label: 'SAG / OTHER', seriesSelector: slot({ modality: 'MR', prefer: ['sag', 'radial', 'oblique'], exclude: ['loc', 'localiz', 'scout'], fallback: 'fourth' }), wl: WL.AUTO },
    ],
  },

  {
    id: 'mr-elbow',
    name: 'MR Elbow',
    priority: 33,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['elbow', 'ulnar collateral', 'ucl'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'COR FS', seriesSelector: slot({ modality: 'MR', prefer: ['cor pd fs', 'cor t2 fs', 'cor fspd', 'coronal'], exclude: ['loc', 'localiz', 'scout', 'sag', 'ax', 't1'], fallback: 'first' }), wl: WL.AUTO },
      { label: 'AX FS', seriesSelector: slot({ modality: 'MR', prefer: ['ax pd fs', 'ax t2 fs', 'ax fspd', 'axial'], exclude: ['loc', 'localiz', 'scout', 'sag', 'cor', 't1'], fallback: 'second' }), wl: WL.AUTO },
      { label: 'SAG FS', seriesSelector: slot({ modality: 'MR', prefer: ['sag pd fs', 'sag t2 fs', 'sag fspd', 'sagittal'], exclude: ['loc', 'localiz', 'scout', 'cor', 'ax', 't1'], fallback: 'third' }), wl: WL.AUTO },
      { label: 'T1', seriesSelector: slot({ modality: 'MR', prefer: ['t1'], exclude: ['loc', 'localiz', 'scout'], fallback: 'fourth' }), wl: WL.MR_T1 },
    ],
  },

  {
    id: 'mr-msk',
    name: 'MR Musculoskeletal',
    priority: 15,
    matchingRules: {
      modalities: ['MR'],
      bodyPartKeywords: ['msk', 'musculo'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'FS SERIES 1', seriesSelector: slot({ modality: 'MR', prefer: ['pd fs', 't2 fs', 'stir', 'fspd', 'fat sat', 'fatsat'], exclude: ['loc', 'localiz', 'scout'], fallback: 'first' }), wl: WL.AUTO },
      { label: 'FS SERIES 2', seriesSelector: slot({ modality: 'MR', prefer: ['pd fs', 't2 fs', 'stir', 'fspd', 'fat sat', 'fatsat'], exclude: ['loc', 'localiz', 'scout'], fallback: 'second' }), wl: WL.AUTO },
      { label: 'T1', seriesSelector: slot({ modality: 'MR', prefer: ['t1'], exclude: ['loc', 'localiz', 'scout'], fallback: 'third' }), wl: WL.MR_T1 },
      { label: 'OTHER', seriesSelector: slot({ modality: 'MR', exclude: ['loc', 'localiz', 'scout'], fallback: 'fourth' }), wl: WL.AUTO },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MR GENERAL (fallback for any MR)
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'mr-general',
    name: 'MR General',
    priority: 5,
    matchingRules: {
      modalities: ['MR'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'SERIES 1', seriesSelector: slot({ fallback: 'first'  }), wl: WL.AUTO },
      { label: 'SERIES 2', seriesSelector: slot({ fallback: 'second' }), wl: WL.AUTO },
      { label: 'SERIES 3', seriesSelector: slot({ fallback: 'third'  }), wl: WL.AUTO },
      { label: 'SERIES 4', seriesSelector: slot({ fallback: 'fourth' }), wl: WL.AUTO },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // CHEST X-RAY (CR / DX)
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'xray-chest',
    name: 'Chest X-Ray',
    priority: 20,
    matchingRules: {
      modalities: ['CR', 'DX'],
      bodyPartKeywords: ['chest', 'thorax', 'lung', 'cxr'],
    },
    layout: { rows: 1, cols: 2 },
    viewports: [
      {
        label: 'PA',
        seriesSelector: slot({
          prefer:  ['pa', 'posteroanterior', 'ap', 'frontal', 'chest'],
          fallback: 'first',
        }),
        wl: WL.CR_DX,
      },
      {
        label: 'LATERAL',
        seriesSelector: slot({
          prefer:  ['lat', 'lateral', 'left lateral', 'right lateral'],
          fallback: 'second',
        }),
        wl: WL.CR_DX,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // X-RAY GENERAL (CR / DX fallback)
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'xray-general',
    name: 'X-Ray',
    priority: 5,
    matchingRules: {
      modalities: ['CR', 'DX'],
    },
    layout: { rows: 1, cols: 1 },
    viewports: [
      {
        label: 'X-RAY',
        seriesSelector: slot({ fallback: 'first' }),
        wl: WL.CR_DX,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MAMMOGRAPHY TOMOSYNTHESIS
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'mammography-tomo',
    name: 'Mammography Tomosynthesis',
    priority: 55,
    matchingRules: {
      modalities: ['MG'],
      bodyPartKeywords: ['tomo', 'dbt', 'tomosynthesis', 'breast tomosynthesis', 'bto'],
      requiredSeries: [
        { modality: 'MG', laterality: 'l', keywords: ['tomo', 'dbt', 'tomosynthesis', 'breast tomosynthesis', 'bto'] },
        { modality: 'MG', laterality: 'r', keywords: ['tomo', 'dbt', 'tomosynthesis', 'breast tomosynthesis', 'bto'] },
      ],
    },
    layout: { rows: 1, cols: 2 },
    viewports: [
      {
        label: 'LEFT TOMO',
        seriesSelector: slot({
          modality: 'MG',
          laterality: 'l',
          requireAny: ['tomo', 'dbt', 'tomosynthesis', 'breast tomosynthesis', 'bto'],
          prefer: ['left tomo', 'l tomo', 'lt tomo', 'lcc tomo', 'lmlo tomo', 'left dbt', 'l dbt', 'tomosynthesis', 'dbt', 'bto'],
          fallback: 'largest',
        }),
        wl: WL.MG,
      },
      {
        label: 'RIGHT TOMO',
        seriesSelector: slot({
          modality: 'MG',
          laterality: 'r',
          requireAny: ['tomo', 'dbt', 'tomosynthesis', 'breast tomosynthesis', 'bto'],
          prefer: ['right tomo', 'r tomo', 'rt tomo', 'rcc tomo', 'rmlo tomo', 'right dbt', 'r dbt', 'tomosynthesis', 'dbt', 'bto'],
          fallback: 'largest',
        }),
        wl: WL.MG,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // MAMMOGRAPHY
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'mammography',
    name: 'Mammography',
    priority: 25,
    matchingRules: {
      modalities: ['MG'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'R CC',
        seriesSelector: slot({ modality: 'MG', prefer: ['rcc', 'r cc', 'right cc', 'right cranio'], fallback: 'first' }),
        wl: WL.MG,
      },
      {
        label: 'L CC',
        seriesSelector: slot({ modality: 'MG', prefer: ['lcc', 'l cc', 'left cc', 'left cranio'], fallback: 'second' }),
        wl: WL.MG,
      },
      {
        label: 'R MLO',
        seriesSelector: slot({ modality: 'MG', prefer: ['rmlo', 'r mlo', 'right mlo', 'right medio'], fallback: 'third' }),
        wl: WL.MG,
      },
      {
        label: 'L MLO',
        seriesSelector: slot({ modality: 'MG', prefer: ['lmlo', 'l mlo', 'left mlo', 'left medio'], fallback: 'fourth' }),
        wl: WL.MG,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ULTRASOUND
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'us-general',
    name: 'Ultrasound',
    priority: 10,
    matchingRules: {
      modalities: ['US'],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: 'GRAY / KEY', seriesSelector: slot({ modality: 'US', prefer: ['gray', 'greyscale', 'b-mode', 'key', 'long', 'trans', 'sag'], exclude: ['report', 'worksheet'], fallback: 'first'  }), wl: WL.US },
      { label: 'CINE', seriesSelector: slot({ modality: 'US', prefer: ['cine', 'clip', 'loop'], exclude: ['report', 'worksheet'], fallback: 'second' }), wl: WL.US },
      { label: 'COLOR / DOPPLER', seriesSelector: slot({ modality: 'US', prefer: ['color', 'doppler', 'power', 'spectral', 'flow'], exclude: ['report', 'worksheet'], fallback: 'third'  }), wl: WL.US },
      { label: 'MEASURE / OTHER', seriesSelector: slot({ modality: 'US', prefer: ['measure', 'caliper', 'annotation', 'worksheet'], fallback: 'fourth' }), wl: WL.US },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // PET / CT
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'pet-ct',
    name: 'PET/CT',
    priority: 30,
    matchingRules: {
      modalities: ['PT', 'PET'],
      requiredSeries: [
        { modality: ['PT', 'PET'] },
        { modality: 'CT' },
      ],
    },
    layout: { rows: 2, cols: 2 },
    viewports: [
      {
        label: 'CT AXIAL',
        seriesSelector: slot({ modality: 'CT', prefer: ['ct', 'attenuation', 'low dose', 'axial'], exclude: ['scout', 'localizer', 'topogram'], fallback: 'first-ct' }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'PET AXIAL',
        seriesSelector: slot({ modality: ['PT', 'PET'], prefer: ['ac', 'attenuation corrected', 'pet', 'fdg', 'wb'], exclude: ['mip', 'maximum intensity', 'nac'], fallback: 'largest' }),
        wl: WL.PET,
      },
      {
        label: 'FUSION',
        seriesSelector: slot({ modality: 'CT', prefer: ['ct', 'attenuation', 'low dose', 'axial'], allowDupe: true, fallback: 'first-ct' }),
        wl: WL.CT_SOFT,
      },
      {
        label: 'PET MIP',
        seriesSelector: slot({ modality: ['PT', 'PET'], prefer: ['mip', 'maximum intensity', 'projection'], allowScouts: true, fallback: 'second-largest' }),
        wl: WL.PET,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // NUCLEAR MEDICINE / PET FALLBACK
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'nm-pet',
    name: 'Nuclear Medicine / PET',
    priority: 15,
    matchingRules: {
      modalities: ['NM', 'PT', 'PET'],
    },
    layout: { rows: 1, cols: 2 },
    viewports: [
      {
        label: 'PET/NM',
        seriesSelector: slot({ modality: ['NM', 'PT', 'PET'], fallback: 'first' }),
        wl: WL.PET,
      },
      {
        label: 'CT / COMPANION',
        seriesSelector: slot({
          modality: 'CT',
          prefer: ['ct', 'attenuation'],
          fallback: 'first-ct',
        }),
        wl: WL.CT_SOFT,
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // DEFAULT FALLBACK
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'default',
    name: 'Default',
    priority: 0,
    matchingRules: {},
    layout: { rows: 2, cols: 2 },
    viewports: [
      { label: '', seriesSelector: slot({ fallback: 'first'  }), wl: WL.AUTO },
      { label: '', seriesSelector: slot({ fallback: 'second' }), wl: WL.AUTO },
      { label: '', seriesSelector: slot({ fallback: 'third'  }), wl: WL.AUTO },
      { label: '', seriesSelector: slot({ fallback: 'fourth' }), wl: WL.AUTO },
    ],
  },
];

export default protocols;
